import { useState, useEffect } from 'react'
import { testesService } from '../services/database'
import { supabase } from '../lib/supabaseClient'
import {
  ChartBarIcon,
  DocumentChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  TrophyIcon,
  ClockIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline'
import type { Database } from '../lib/supabaseClient'

type Resultado = Database['public']['Tables']['resultados']['Row']
type Teste = Database['public']['Tables']['testes']['Row']

interface EstatisticasGerais {
  total_usuarios: number
  total_testes: number
  total_tentativas: number
  media_geral: number
  taxa_aprovacao: number
  tempo_medio: number
}

interface EstatisticasTeste {
  teste_id: string
  teste_title: string
  total_tentativas: number
  media_score: number
  taxa_aprovacao: number
  tempo_medio: number
}

const RelatoriosPage = () => {
  const [resultados, setResultados] = useState<Resultado[]>([])
  const [testes, setTestes] = useState<Teste[]>([])
  const [loading, setLoading] = useState(true)
  const [filtroTeste, setFiltroTeste] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroData, setFiltroData] = useState('')
  const [estatisticasGerais, setEstatisticasGerais] = useState<EstatisticasGerais>({
    total_usuarios: 0,
    total_testes: 0,
    total_tentativas: 0,
    media_geral: 0,
    taxa_aprovacao: 0,
    tempo_medio: 0
  })
  const [estatisticasTestes, setEstatisticasTestes] = useState<EstatisticasTeste[]>([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [resultadosResponse, testesData] = await Promise.all([
        supabase.from('resultados').select('*').order('created_at', { ascending: false }),
        testesService.getAll()
      ])
      
      const resultadosData = resultadosResponse.data || []
      setResultados(resultadosData)
      setTestes(testesData)
      
      // Calcular estatísticas
      calcularEstatisticas(resultadosData, testesData)
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  const calcularEstatisticas = (resultados: Resultado[], testes: Teste[]) => {
    // Estatísticas gerais
    const usuariosUnicos = new Set(resultados.map(r => r.aluno_id).filter(Boolean)).size
    const totalTentativas = resultados.length
    const mediaGeral = resultados.length > 0 
      ? resultados.reduce((acc, r) => acc + (r.percentual_acertos || 0), 0) / resultados.length 
      : 0
    const aprovados = resultados.filter(r => (r.percentual_acertos || 0) >= 70).length
    const taxaAprovacao = totalTentativas > 0 ? (aprovados / totalTentativas) * 100 : 0
    const tempoMedio = resultados.length > 0
      ? resultados.reduce((acc, r) => {
          const inicio = r.started_at ? new Date(r.started_at) : null
          const fim = r.completed_at ? new Date(r.completed_at) : null
          if (inicio && fim) {
            return acc + (fim.getTime() - inicio.getTime()) / (1000 * 60) // em minutos
          }
          return acc
        }, 0) / resultados.length
      : 0

    setEstatisticasGerais({
      total_usuarios: usuariosUnicos,
      total_testes: testes.length,
      total_tentativas: totalTentativas,
      media_geral: mediaGeral,
      taxa_aprovacao: taxaAprovacao,
      tempo_medio: tempoMedio
    })

    // Estatísticas por teste
    const estatisticasPorTeste = testes.map(teste => {
      const resultadosTeste = resultados.filter(r => r.teste_id === teste.id)
      const totalTentativas = resultadosTeste.length
      const mediaScore = totalTentativas > 0
        ? resultadosTeste.reduce((acc, r) => acc + (r.percentual_acertos || 0), 0) / totalTentativas
        : 0
      const aprovados = resultadosTeste.filter(r => (r.percentual_acertos || 0) >= 70).length
      const taxaAprovacao = totalTentativas > 0 ? (aprovados / totalTentativas) * 100 : 0
      const tempoMedio = totalTentativas > 0
        ? resultadosTeste.reduce((acc, r) => {
            const inicio = r.started_at ? new Date(r.started_at) : null
            const fim = r.completed_at ? new Date(r.completed_at) : null
            if (inicio && fim) {
              return acc + (fim.getTime() - inicio.getTime()) / (1000 * 60) // em minutos
            }
            return acc
          }, 0) / totalTentativas
        : 0

      return {
        teste_id: teste.id,
        teste_title: teste.title,
        total_tentativas: totalTentativas,
        media_score: mediaScore,
        taxa_aprovacao: taxaAprovacao,
        tempo_medio: tempoMedio
      }
    })

    setEstatisticasTestes(estatisticasPorTeste)
  }

  const filteredResultados = resultados.filter(resultado => {
    const matchesTeste = !filtroTeste || resultado.teste_id === filtroTeste
    const matchesStatus = !filtroStatus || getStatusFromResult(resultado) === filtroStatus
    const matchesData = !filtroData || 
      (resultado.created_at && new Date(resultado.created_at).toISOString().split('T')[0] === filtroData)
    
    return matchesTeste && matchesStatus && matchesData
  })

  const getStatusFromResult = (resultado: Resultado) => {
    if (resultado.completed_at) return 'completed'
    if (resultado.started_at) return 'in_progress'
    return 'abandoned'
  }

  const getTesteTitle = (testeId: string) => {
    if (!testeId) return 'N/A'
    const teste = testes.find(t => t.id === testeId)
    return teste?.title || 'Teste não encontrado'
  }

  const formatTempo = (minutos: number) => {
    if (minutos < 60) {
      return `${Math.round(minutos)}min`
    }
    const horas = Math.floor(minutos / 60)
    const mins = Math.round(minutos % 60)
    return `${horas}h ${mins}min`
  }

  const formatData = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const exportarRelatorio = () => {
    const csvContent = [
      ['Aluno', 'Teste', 'Pontuação', 'Status', 'Tempo Gasto', 'Data'].join(','),
      ...filteredResultados.map(resultado => {
        const status = getStatusFromResult(resultado)
        const tempoGasto = resultado.started_at && resultado.completed_at 
          ? Math.round((new Date(resultado.completed_at).getTime() - new Date(resultado.started_at).getTime()) / (1000 * 60))
          : 0
        return [
          resultado.aluno_id || 'N/A',
          getTesteTitle(resultado.teste_id || ''),
          resultado.percentual_acertos || 0,
          status === 'completed' ? 'Concluído' : status === 'in_progress' ? 'Em andamento' : 'Abandonado',
          formatTempo(tempoGasto),
          resultado.created_at ? formatData(resultado.created_at) : 'N/A'
        ].join(',')
      })
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `relatorio_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-600">Análise de desempenho e estatísticas dos testes</p>
        </div>
        <button
          onClick={exportarRelatorio}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <ArrowDownTrayIcon className="h-5 w-5" />
          <span>Exportar CSV</span>
        </button>
      </div>

      {/* Estatísticas Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Usuários</p>
              <p className="text-2xl font-bold text-gray-900">{estatisticasGerais.total_usuarios}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex items-center">
            <ClipboardDocumentListIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Testes</p>
              <p className="text-2xl font-bold text-gray-900">{estatisticasGerais.total_testes}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex items-center">
            <DocumentChartBarIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Tentativas</p>
              <p className="text-2xl font-bold text-gray-900">{estatisticasGerais.total_tentativas}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Média Geral</p>
              <p className="text-2xl font-bold text-gray-900">{estatisticasGerais.media_geral.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex items-center">
            <TrophyIcon className="h-8 w-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Taxa Aprovação</p>
              <p className="text-2xl font-bold text-gray-900">{estatisticasGerais.taxa_aprovacao.toFixed(1)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Tempo Médio</p>
              <p className="text-2xl font-bold text-gray-900">{formatTempo(estatisticasGerais.tempo_medio)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Estatísticas por Teste */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Desempenho por Teste</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Teste
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tentativas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Média
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taxa Aprovação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tempo Médio
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {estatisticasTestes.map((estatistica) => (
                <tr key={estatistica.teste_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{estatistica.teste_title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{estatistica.total_tentativas}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{estatistica.media_score.toFixed(1)}%</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      estatistica.taxa_aprovacao >= 70 
                        ? 'bg-green-100 text-green-800' 
                        : estatistica.taxa_aprovacao >= 50
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {estatistica.taxa_aprovacao.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatTempo(estatistica.tempo_medio)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center space-x-2 mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teste
            </label>
            <select
              value={filtroTeste}
              onChange={(e) => setFiltroTeste(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos os testes</option>
              {testes.map((teste) => (
                <option key={teste.id} value={teste.id}>
                  {teste.title}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos os status</option>
              <option value="completed">Concluído</option>
              <option value="in_progress">Em andamento</option>
              <option value="abandoned">Abandonado</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data
            </label>
            <input
              type="date"
              value={filtroData}
              onChange={(e) => setFiltroData(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Tabela de Resultados */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Resultados Detalhados ({filteredResultados.length})
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aluno
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Teste
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pontuação
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tempo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredResultados.map((resultado) => {
                const status = getStatusFromResult(resultado)
                const tempoGasto = resultado.started_at && resultado.completed_at 
                  ? Math.round((new Date(resultado.completed_at).getTime() - new Date(resultado.started_at).getTime()) / (1000 * 60))
                  : 0
                return (
                  <tr key={resultado.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{resultado.aluno_id || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{getTesteTitle(resultado.teste_id || '')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {resultado.percentual_acertos ? `${resultado.percentual_acertos}%` : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        status === 'completed' 
                          ? 'bg-green-100 text-green-800'
                          : status === 'in_progress'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {status === 'completed' ? 'Concluído' :
                         status === 'in_progress' ? 'Em andamento' : 'Abandonado'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {tempoGasto ? formatTempo(tempoGasto) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {resultado.created_at ? formatData(resultado.created_at) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        className="text-blue-600 hover:text-blue-800"
                        title="Ver detalhes"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        
        {filteredResultados.length === 0 && (
          <div className="text-center py-12">
            <DocumentChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum resultado encontrado</h3>
            <p className="text-gray-600">
              {filtroTeste || filtroStatus || filtroData 
                ? 'Tente ajustar os filtros para ver mais resultados'
                : 'Ainda não há resultados de testes para exibir'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default RelatoriosPage